export type ThemeMode = "light" | "dark";

const BLUE_500 = "#1668ff";
const BLUE_600 = "#0b4fd6";
const BLUE_300 = "#5f95ff";
const BLUE_50 = "#e9f0ff";
const ORANGE_500 = "#f26522";
const ORANGE_600 = "#d24f13";
const ORANGE_50 = "#fdf1e9";
const MAGENTA = "#e0189e";
const YELLOW = "#ffc629";

export interface OpsTokens {
  blue500: string;
  blue600: string;
  blue300: string;
  blue50: string;
  orange500: string;
  orange600: string;
  orange50: string;
  magenta: string;
  yellow: string;

  actionPrimary: string;
  actionPrimaryHover: string;
  actionPrimaryActive: string;
  brandAccent: string;
  timeoffFg: string;
  timeoffBg: string;
  textLink: string;
  textLinkHover: string;
  textAccent: string;
  borderFocus: string;
  focusRing: string;

  surfaceApp: string;
  surfaceCard: string;
  surfaceSunken: string;
  surfaceRaised: string;
  surfaceHover: string;
  surfaceActive: string;
  surfaceSelected: string;

  borderSubtle: string;
  borderDefault: string;
  borderStrong: string;

  textPrimary: string;
  textSecondary: string;
  textTertiary: string;

  statusSuccessFg: string;
  statusSuccessBg: string;
  statusWarningFg: string;
  statusWarningBg: string;
  statusDangerFg: string;
  statusDangerBg: string;
  statusInfoFg: string;
  statusInfoBg: string;
  statusProgressFg: string;
  statusProgressBg: string;

  textOnAction: string;
  surfaceInverse: string;
  textInverse: string;
  tableHeaderBg: string;
  topbarBg: string;
  topbarFg: string;

  fontSans: string;
  fontMono: string;
  textBase: number;
  rowHeight: number;
  controlMd: number;
  cardHeader: number;
  radiusChip: number;
  radiusControl: number;
  radiusCard: number;
  radiusDialog: number;
  gutter: number;
  cardPad: number;
  ease: string;
  drawerPanelWidth: number;
}

const shared = {
  blue500: BLUE_500,
  blue600: BLUE_600,
  blue300: BLUE_300,
  blue50: BLUE_50,
  orange500: ORANGE_500,
  orange600: ORANGE_600,
  orange50: ORANGE_50,
  magenta: MAGENTA,
  yellow: YELLOW,

  brandAccent: ORANGE_500,
  borderFocus: BLUE_300,
  focusRing: "rgba(22,104,255,.40)",

  topbarFg: "#9aa3b1",

  textOnAction: "#ffffff",

  fontSans: '"Figtree", system-ui, sans-serif',
  fontMono: '"IBM Plex Mono", ui-monospace, monospace',
  textBase: 13,
  rowHeight: 44,
  controlMd: 32,
  cardHeader: 36,
  radiusChip: 3,
  radiusControl: 5,
  radiusCard: 8,
  radiusDialog: 12,
  gutter: 16,
  cardPad: 12,
  ease: "cubic-bezier(.2,0,.2,1)",
  drawerPanelWidth: 560,
};

export const lightTokens: OpsTokens = {
  ...shared,

  actionPrimary: BLUE_500,
  actionPrimaryHover: BLUE_600,
  actionPrimaryActive: "#083da8",
  timeoffFg: MAGENTA,
  timeoffBg: "#fde8f5",
  textLink: BLUE_600,
  textLinkHover: BLUE_500,
  textAccent: BLUE_600,

  surfaceApp: "#f4f6f8",
  surfaceCard: "#ffffff",
  surfaceSunken: "#eaedf1",
  surfaceRaised: "#ffffff",
  surfaceHover: "#f4f6f8",
  surfaceActive: "#eaedf1",
  surfaceSelected: BLUE_50,

  borderSubtle: "#eaedf1",
  borderDefault: "#dde1e8",
  borderStrong: "#c6ccd6",

  textPrimary: "#171d26",
  textSecondary: "#515a69",
  textTertiary: "#6d7686",

  statusSuccessFg: "#1f8c53",
  statusSuccessBg: "#e8f5ee",
  statusWarningFg: "#b7791f",
  statusWarningBg: "#fdf4e3",
  statusDangerFg: "#cf2436",
  statusDangerBg: "#fdeced",
  statusInfoFg: BLUE_600,
  statusInfoBg: BLUE_50,
  statusProgressFg: "#9c3a0c",
  statusProgressBg: ORANGE_50,

  surfaceInverse: "#171d26",
  textInverse: "#ffffff",
  tableHeaderBg: "#fafbfc",
  topbarBg: "#0b0d10",
};

export const darkTokens: OpsTokens = {
  ...shared,

  actionPrimary: BLUE_500,
  actionPrimaryHover: BLUE_300,
  actionPrimaryActive: BLUE_600,
  timeoffFg: "#f174c4",
  timeoffBg: "rgba(224,24,158,.18)",
  textLink: BLUE_300,
  textLinkHover: "#9dbcff",
  textAccent: BLUE_300,

  surfaceApp: "#0e1116",
  surfaceCard: "#161a21",
  surfaceSunken: "#12161c",
  surfaceRaised: "#1c2129",
  surfaceHover: "#1c2129",
  surfaceActive: "#232935",
  surfaceSelected: "rgba(22,104,255,.16)",

  borderSubtle: "#212734",
  borderDefault: "#2b323f",
  borderStrong: "#3d4655",

  textPrimary: "#eef1f5",
  textSecondary: "#a6b0bd",
  textTertiary: "#7b8695",

  statusSuccessFg: "#6fcb98",
  statusSuccessBg: "rgba(31,140,83,.16)",
  statusWarningFg: "#e3b866",
  statusWarningBg: "rgba(183,121,31,.16)",
  statusDangerFg: "#f08a94",
  statusDangerBg: "rgba(207,36,54,.16)",
  statusInfoFg: "#8fb2ff",
  statusInfoBg: "rgba(22,104,255,.18)",
  statusProgressFg: "#f9a76b",
  statusProgressBg: "rgba(242,101,34,.16)",

  surfaceInverse: "#eef1f5",
  textInverse: "#171d26",
  tableHeaderBg: "#12161c",
  topbarBg: "#08090c",
};

export const TOKENS: Record<ThemeMode, OpsTokens> = {
  light: lightTokens,
  dark: darkTokens,
};

export const CSS_VAR_OF: Record<keyof OpsTokens, string> = {
  blue500: "--ens-blue-500",
  blue600: "--ens-blue-600",
  blue300: "--ens-blue-300",
  blue50: "--ens-blue-50",
  orange500: "--ens-orange-500",
  orange600: "--ens-orange-600",
  orange50: "--ens-orange-50",
  magenta: "--ens-magenta",
  yellow: "--ens-yellow",

  actionPrimary: "--action-primary",
  actionPrimaryHover: "--action-primary-hover",
  actionPrimaryActive: "--action-primary-active",
  brandAccent: "--brand-accent",
  timeoffFg: "--timeoff-fg",
  timeoffBg: "--timeoff-bg",
  textLink: "--text-link",
  textLinkHover: "--text-link-hover",
  textAccent: "--text-accent",
  borderFocus: "--border-focus",
  focusRing: "--focus-ring",

  surfaceApp: "--surface-app",
  surfaceCard: "--surface-card",
  surfaceSunken: "--surface-sunken",
  surfaceRaised: "--surface-raised",
  surfaceHover: "--surface-hover",
  surfaceActive: "--surface-active",
  surfaceSelected: "--surface-selected",

  borderSubtle: "--border-subtle",
  borderDefault: "--border-default",
  borderStrong: "--border-strong",

  textPrimary: "--text-primary",
  textSecondary: "--text-secondary",
  textTertiary: "--text-tertiary",

  statusSuccessFg: "--status-success-fg",
  statusSuccessBg: "--status-success-bg",
  statusWarningFg: "--status-warning-fg",
  statusWarningBg: "--status-warning-bg",
  statusDangerFg: "--status-danger-fg",
  statusDangerBg: "--status-danger-bg",
  statusInfoFg: "--status-info-fg",
  statusInfoBg: "--status-info-bg",
  statusProgressFg: "--status-progress-fg",
  statusProgressBg: "--status-progress-bg",

  textOnAction: "--text-on-action",
  surfaceInverse: "--surface-inverse",
  textInverse: "--text-inverse",
  tableHeaderBg: "--table-header-bg",
  topbarBg: "--topbar-bg",
  topbarFg: "--topbar-fg",

  fontSans: "--font-sans",
  fontMono: "--font-mono",
  textBase: "--text-base",
  rowHeight: "--row-height",
  controlMd: "--control-md",
  cardHeader: "--card-header",
  radiusChip: "--radius-chip",
  radiusControl: "--radius-control",
  radiusCard: "--radius-card",
  radiusDialog: "--radius-dialog",
  gutter: "--gutter",
  cardPad: "--card-pad",
  ease: "--ease",
  drawerPanelWidth: "--drawer-panel-width",
};

export const PX_TOKENS: ReadonlySet<keyof OpsTokens> = new Set([
  "textBase",
  "rowHeight",
  "controlMd",
  "cardHeader",
  "radiusChip",
  "radiusControl",
  "radiusCard",
  "radiusDialog",
  "gutter",
  "cardPad",
  "drawerPanelWidth",
] as Array<keyof OpsTokens>);

export const cssValueOf = (key: keyof OpsTokens, tokens: OpsTokens): string =>
  PX_TOKENS.has(key) ? `${tokens[key]}px` : String(tokens[key]);

export const DEFAULT_THEME_MODE: ThemeMode = "light";
